(() => {
  const configuration = window.HimalayansTheme || {};
  const routes = configuration.routes || {};
  const strings = configuration.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const state = {
    toastTimer: null,
    activeRoot: null,
    catalogueStates: new WeakMap(),
    rotatingTimers: new WeakMap(),
    cartInputTimers: new WeakMap()
  };

  const selectAll = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const select = (selector, context = document) => context.querySelector(selector);

  const toAjaxRoute = (route) => {
    if (!route) return '';
    return route.endsWith('.js') ? route : `${route}.js`;
  };

  const focusableElements = (root) => selectAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', root).filter((element) => !element.hidden && element.offsetParent !== null);

  const syncExpandedControls = (root, expanded) => {
    if (!root.id) return;
    selectAll('[aria-controls]').forEach((control) => {
      if (control.getAttribute('aria-controls') === root.id) control.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  const releaseScrollLock = () => {
    if (!select('.drawer.is-open, .cart-drawer.is-open, .facets-drawer.is-open')) document.body.classList.remove('drawer-active');
  };

  const closeInteractiveRoot = (root, restoreFocus = true) => {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    syncExpandedControls(root, false);
    if (state.activeRoot === root) state.activeRoot = null;
    releaseScrollLock();
    const returnFocus = root.__returnFocus;
    root.__returnFocus = null;
    if (restoreFocus && returnFocus && document.contains(returnFocus)) window.setTimeout(() => returnFocus.focus(), 0);
  };

  const openInteractiveRoot = (root, trigger = null) => {
    if (!root) return;
    selectAll('.drawer.is-open, .cart-drawer.is-open, .facets-drawer.is-open').forEach((openRoot) => {
      if (openRoot !== root) closeInteractiveRoot(openRoot, false);
    });
    root.__returnFocus = trigger || document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    syncExpandedControls(root, true);
    document.body.classList.add('drawer-active');
    state.activeRoot = root;
    window.requestAnimationFrame(() => {
      const preferred = select('[data-drawer-autofocus]', root);
      const panel = select('[role="dialog"]', root) || select('.drawer__panel, .cart-drawer__panel, .facets', root);
      const target = preferred || focusableElements(root)[0] || panel;
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    });
  };

  const showToast = (message) => {
    const toast = select('[data-toast]');
    if (!toast || !message) return;
    window.clearTimeout(state.toastTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    state.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const setFormMessage = (form, message, type) => {
    const output = select('.form-message', form);
    if (!output) return;
    output.textContent = message || '';
    output.classList.toggle('form-message--error', type === 'error');
    output.classList.toggle('form-message--success', type === 'success');
  };

  const parseJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch (error) {
      return { description: text };
    }
  };

  const updateCartBubbles = (count) => {
    const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    selectAll('[data-cart-count-bubble]').forEach((bubble) => {
      bubble.textContent = safeCount;
      bubble.classList.toggle('is-hidden', safeCount === 0);
    });
  };

  const replaceCartDrawer = (html, shouldOpen = true, trigger = null) => {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = select('[data-cart-drawer-root]', parsed);
    const currentRoot = select('[data-cart-drawer-root]');
    if (!nextRoot || !currentRoot) return null;
    const importedRoot = document.importNode(nextRoot, true);
    currentRoot.replaceWith(importedRoot);
    updateCartBubbles(Number(importedRoot.dataset.cartCount || 0));
    if (shouldOpen) openInteractiveRoot(importedRoot, trigger);
    return importedRoot;
  };

  const requestCartDrawer = async (shouldOpen = true, trigger = null) => {
    const root = routes.root || '/';
    const separator = root.includes('?') ? '&' : '?';
    const response = await fetch(`${root}${separator}sections=cart-drawer`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) throw new Error(data.description || strings.error || 'Unable to load the cart.');
    return replaceCartDrawer(data['cart-drawer'], shouldOpen, trigger);
  };

  const handleAddToCart = async (form, submitter) => {
    const button = submitter && submitter.matches('[data-add-to-cart]') ? submitter : select('[data-add-to-cart]', form);
    if (!button || button.disabled) return;
    const initiallyDisabled = button.disabled;
    const formData = new FormData(form);
    formData.set('sections', 'cart-drawer');
    formData.set('sections_url', window.location.pathname);
    button.disabled = true;
    button.classList.add('is-loading');
    form.setAttribute('aria-busy', 'true');
    setFormMessage(form, '', '');
    try {
      const response = await fetch(toAjaxRoute(routes.cartAdd || '/cart/add'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(data.description || data.message || strings.error || 'Unable to add this product.');
      const drawerEnabled = document.body.dataset.cartDrawerEnabled === 'true';
      if (data.sections && data.sections['cart-drawer']) {
        replaceCartDrawer(data.sections['cart-drawer'], drawerEnabled, button);
      } else {
        await requestCartDrawer(drawerEnabled, button);
      }
      setFormMessage(form, strings.added || 'Added to cart.', 'success');
      showToast(strings.added || 'Added to cart.');
      if (!drawerEnabled) window.location.assign(routes.cart || '/cart');
    } catch (error) {
      setFormMessage(form, error.message || strings.error || 'Unable to add this product.', 'error');
    } finally {
      form.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
      button.disabled = initiallyDisabled;
    }
  };

  const handleCartChange = async (line, quantity, trigger = null) => {
    const cartRoot = select('[data-cart-drawer-root]');
    if (!cartRoot) return;
    cartRoot.setAttribute('aria-busy', 'true');
    const errorOutput = select('[data-cart-error]', cartRoot);
    if (errorOutput) errorOutput.textContent = '';
    try {
      const response = await fetch(toAjaxRoute(routes.cartChange || '/cart/change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Math.max(0, Number(quantity) || 0),
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(data.description || data.message || strings.error || 'Unable to update the cart.');
      if (data.sections && data.sections['cart-drawer']) {
        replaceCartDrawer(data.sections['cart-drawer'], true, trigger);
      } else {
        await requestCartDrawer(true, trigger);
      }
    } catch (error) {
      const currentRoot = select('[data-cart-drawer-root]');
      const currentError = currentRoot ? select('[data-cart-error]', currentRoot) : null;
      if (currentError) currentError.textContent = error.message || strings.error || 'Unable to update the cart.';
    } finally {
      const currentRoot = select('[data-cart-drawer-root]');
      if (currentRoot) currentRoot.removeAttribute('aria-busy');
    }
  };

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    const numeric = Number(number);
    if (!Number.isFinite(numeric)) return '0';
    const fixed = (numeric / 100).toFixed(precision);
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return precision ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents) => {
    const format = configuration.moneyFormat || '£{{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(cents);
    const placeholder = match[1];
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ',']
    };
    const options = formats[placeholder] || formats.amount;
    return format.replace(match[0], formatWithDelimiters(cents, options[0], options[1], options[2]));
  };

  const setMedia = (scope, mediaId) => {
    if (!scope || !mediaId) return;
    const requestedId = String(mediaId);
    selectAll('[data-product-media]', scope).forEach((media) => {
      const active = String(media.dataset.productMedia) === requestedId;
      media.classList.toggle('is-active', active);
      media.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (!active) {
        selectAll('video', media).forEach((video) => video.pause());
        selectAll('iframe', media).forEach((iframe) => {
          const source = iframe.getAttribute('src');
          if (source && /youtube|vimeo/.test(source)) iframe.setAttribute('src', source);
        });
      }
    });
    selectAll('[data-media-target]', scope).forEach((thumbnail) => {
      const active = String(thumbnail.dataset.mediaTarget) === requestedId;
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  };

  const updateProductVariant = (section) => {
    const variantData = select('[data-product-variants]', section);
    if (!variantData) return;
    let variants = [];
    try {
      variants = JSON.parse(variantData.textContent || '[]');
    } catch (error) {
      return;
    }
    const selectors = selectAll('[data-product-option]', section).sort((left, right) => Number(left.dataset.optionPosition) - Number(right.dataset.optionPosition));
    const selectedOptions = selectors.map((selector) => selector.value);
    const variant = variants.find((candidate) => Array.isArray(candidate.options) && candidate.options.every((value, index) => String(value) === String(selectedOptions[index])));
    const variantInput = select('[data-variant-id]', section);
    const addButton = select('[data-add-to-cart]', section);
    const buttonLabel = addButton ? select('[data-button-label]', addButton) : null;
    const stock = select('[data-stock-status]', section);
    const price = select('[data-product-price]', section);
    const compare = select('[data-compare-price]', section);
    const priceWrapper = select('[data-price-wrapper]', section);
    const discount = select('[data-discount-badge]', section);
    const gallerySaleBadge = select('[data-gallery-sale-badge]', section);
    const stockLabel = stock ? select('[data-stock-label]', stock) : null;
    const stockIconIn = stock ? select('[data-stock-icon-in]', stock) : null;
    const stockIconOut = stock ? select('[data-stock-icon-out]', stock) : null;
    if (!variant) {
      if (variantInput) variantInput.value = '';
      if (addButton) addButton.disabled = true;
      if (buttonLabel) buttonLabel.textContent = strings.unavailable || 'Unavailable';
      if (stock) stock.classList.add('stock-status--sold');
      if (stockLabel) stockLabel.textContent = strings.unavailable || 'Unavailable';
      if (stockIconIn) stockIconIn.classList.add('is-hidden');
      if (stockIconOut) stockIconOut.classList.remove('is-hidden');
      if (compare) compare.classList.add('is-hidden');
      if (discount) discount.classList.add('is-hidden');
      if (gallerySaleBadge) gallerySaleBadge.classList.add('is-hidden');
      return;
    }
    if (variantInput) variantInput.value = variant.id;
    if (price) price.innerHTML = formatMoney(variant.price);
    if (compare) {
      if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
        compare.innerHTML = formatMoney(variant.compare_at_price);
        compare.classList.remove('is-hidden');
      } else {
        compare.innerHTML = '';
        compare.classList.add('is-hidden');
      }
    }
    if (priceWrapper) {
      priceWrapper.classList.toggle('price--sale', Boolean(variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)));
      priceWrapper.classList.toggle('price--sold-out', !variant.available);
    }
    if (addButton) addButton.disabled = !variant.available;
    if (buttonLabel) buttonLabel.textContent = variant.available ? strings.addToCart || 'Add to cart' : strings.soldOut || 'Sold out';
    if (stock) stock.classList.toggle('stock-status--sold', !variant.available);
    if (stockLabel) stockLabel.textContent = variant.available ? strings.inStock || 'In stock' : strings.soldOut || 'Sold out';
    if (stockIconIn) stockIconIn.classList.toggle('is-hidden', !variant.available);
    if (stockIconOut) stockIconOut.classList.toggle('is-hidden', variant.available);
    const comparePrice = Number(variant.compare_at_price || 0);
    const priceValue = Number(variant.price || 0);
    const percent = comparePrice > priceValue ? Math.round(((comparePrice - priceValue) * 100) / comparePrice) : 0;
    if (discount) {
      const template = strings.savePercent || 'Save __PERCENT__%';
      discount.textContent = template.replace('__PERCENT__', String(percent));
      discount.classList.toggle('is-hidden', percent === 0);
    }
    if (gallerySaleBadge) gallerySaleBadge.classList.toggle('is-hidden', percent === 0);
    const mediaId = variant.featured_media && variant.featured_media.id ? variant.featured_media.id : variant.featured_image && variant.featured_image.id ? variant.featured_image.id : null;
    if (mediaId) setMedia(section, mediaId);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    section.dispatchEvent(new CustomEvent('himalayans:variant-change', { bubbles: true, detail: { variant } }));
  };

  const adjustQuantity = (button, direction) => {
    const control = button.closest('.quantity-control');
    const input = control ? select('input[type="number"]', control) : null;
    if (!input) return;
    const minimum = input.min === '' ? 0 : Number(input.min);
    const maximum = input.max === '' ? Number.POSITIVE_INFINITY : Number(input.max);
    const step = input.step && input.step !== 'any' ? Number(input.step) : 1;
    const current = Number(input.value) || minimum;
    input.value = String(Math.min(maximum, Math.max(minimum, current + direction * step)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const setRotatingTimer = (root, callback, speed) => {
    const existing = state.rotatingTimers.get(root);
    if (existing) window.clearInterval(existing);
    if (reducedMotion.matches || speed < 1000) return;
    const timer = window.setInterval(callback, speed);
    state.rotatingTimers.set(root, timer);
  };

  const initializeAnnouncement = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const slides = selectAll('[data-announcement-slide]', root);
    if (slides.length < 2 || root.dataset.autoplay !== 'true') return;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const advance = () => {
      slides[index].classList.remove('is-active');
      index = (index + 1) % slides.length;
      slides[index].classList.add('is-active');
    };
    const speed = Number(root.dataset.speed || 6000);
    const start = () => setRotatingTimer(root, advance, speed);
    const pause = () => {
      const timer = state.rotatingTimers.get(root);
      if (timer) window.clearInterval(timer);
    };
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', start);
    start();
  };

  const initializeHero = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const slides = selectAll('[data-hero-slide]', root);
    const dots = selectAll('[data-hero-dot]', root);
    if (!slides.length) return;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let pointerStart = null;
    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };
    const next = () => show(index + 1);
    const previous = () => show(index - 1);
    const speed = Number(root.dataset.speed || 5000);
    const start = () => {
      if (root.dataset.autoplay === 'true' && slides.length > 1) setRotatingTimer(root, next, speed);
    };
    const pause = () => {
      const timer = state.rotatingTimers.get(root);
      if (timer) window.clearInterval(timer);
    };
    select('[data-hero-next]', root)?.addEventListener('click', () => {
      next();
      start();
    });
    select('[data-hero-previous]', root)?.addEventListener('click', () => {
      previous();
      start();
    });
    dots.forEach((dot) => dot.addEventListener('click', () => {
      show(Number(dot.dataset.heroDot));
      start();
    }));
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', start);
    root.addEventListener('pointerdown', (event) => {
      pointerStart = event.clientX;
    });
    root.addEventListener('pointerup', (event) => {
      if (pointerStart === null) return;
      const distance = event.clientX - pointerStart;
      pointerStart = null;
      if (Math.abs(distance) < 55) return;
      if (distance > 0) previous(); else next();
      start();
    });
    start();
  };

  const initializeCatalogue = (root) => {
    if (state.catalogueStates.has(root)) return;
    const items = selectAll('[data-catalogue-item]', root);
    const grid = select('[data-catalogue-grid]', root);
    if (!grid || !items.length) {
      const load = select('[data-catalogue-load]', root);
      const count = select('[data-catalogue-count]', root);
      if (load) load.hidden = true;
      if (count) count.textContent = 'Select a collection to show products.';
      return;
    }
    items.forEach((item) => item.classList.remove('catalogue-card--initial-hidden'));
    const catalogueState = {
      visible: Number(root.dataset.initialCount || 16),
      initial: Number(root.dataset.initialCount || 16),
      increment: Number(root.dataset.loadCount || 16)
    };
    state.catalogueStates.set(root, catalogueState);
    const controls = {
      search: selectAll('[data-catalogue-search]', root),
      type: selectAll('[data-catalogue-type]', root),
      availability: selectAll('[data-catalogue-availability]', root),
      price: selectAll('[data-catalogue-price]', root),
      sale: selectAll('[data-catalogue-sale]', root),
      sort: selectAll('[data-catalogue-sort]', root)
    };
    const syncControlGroup = (group, source) => {
      group.forEach((control) => {
        if (control === source) return;
        if (control.type === 'checkbox') control.checked = source.checked;
        else control.value = source.value;
      });
    };
    const getValue = (group) => group[0] ? group[0].value : '';
    const getChecked = (group) => group[0] ? group[0].checked : false;
    const apply = () => {
      const query = getValue(controls.search).trim().toLowerCase();
      const type = getValue(controls.type);
      const availability = getValue(controls.availability);
      const priceRange = getValue(controls.price);
      const saleOnly = getChecked(controls.sale);
      const sort = getValue(controls.sort) || 'featured';
      const matching = items.filter((item) => {
        const card = select('[data-product-card]', item);
        if (!card) return false;
        const haystack = `${card.dataset.title || ''} ${card.dataset.type || ''} ${card.dataset.vendor || ''}`.toLowerCase();
        const productPrice = Number(card.dataset.price || 0);
        const matchesQuery = !query || haystack.includes(query);
        const matchesType = !type || card.dataset.type === type;
        const matchesAvailability = !availability || card.dataset.available === availability;
        const matchesSale = !saleOnly || card.dataset.sale === 'true';
        let matchesPrice = true;
        if (priceRange === 'under-1000') matchesPrice = productPrice < 1000;
        if (priceRange === '1000-2500') matchesPrice = productPrice >= 1000 && productPrice <= 2500;
        if (priceRange === 'over-2500') matchesPrice = productPrice > 2500;
        return matchesQuery && matchesType && matchesAvailability && matchesSale && matchesPrice;
      });
      const sorted = [...items].sort((left, right) => {
        const leftCard = select('[data-product-card]', left);
        const rightCard = select('[data-product-card]', right);
        const leftPrice = Number(leftCard?.dataset.price || 0);
        const rightPrice = Number(rightCard?.dataset.price || 0);
        const leftTitle = leftCard?.dataset.title || '';
        const rightTitle = rightCard?.dataset.title || '';
        const leftCreated = Number(leftCard?.dataset.created || 0);
        const rightCreated = Number(rightCard?.dataset.created || 0);
        if (sort === 'price-ascending') return leftPrice - rightPrice;
        if (sort === 'price-descending') return rightPrice - leftPrice;
        if (sort === 'title-ascending') return leftTitle.localeCompare(rightTitle);
        if (sort === 'created-descending') return rightCreated - leftCreated;
        return Number(left.dataset.index || 0) - Number(right.dataset.index || 0);
      });
      sorted.forEach((item) => grid.appendChild(item));
      const matchingSet = new Set(matching);
      let displayed = 0;
      sorted.forEach((item) => {
        const visible = matchingSet.has(item) && displayed < catalogueState.visible;
        item.hidden = !visible;
        if (visible) displayed += 1;
      });
      const count = select('[data-catalogue-count]', root);
      const empty = select('[data-catalogue-empty]', root);
      const load = select('[data-catalogue-load]', root);
      if (count) count.textContent = matching.length ? `Showing ${displayed} of ${matching.length} products` : 'No products match these filters.';
      if (empty) empty.hidden = matching.length !== 0;
      if (load) load.hidden = displayed >= matching.length;
    };
    Object.values(controls).forEach((group) => {
      group.forEach((control) => {
        const eventName = control.matches('input[type="search"]') ? 'input' : 'change';
        control.addEventListener(eventName, () => {
          syncControlGroup(group, control);
          catalogueState.visible = catalogueState.initial;
          apply();
        });
      });
    });
    selectAll('[data-catalogue-clear]', root).forEach((button) => button.addEventListener('click', () => {
      controls.search.forEach((control) => { control.value = ''; });
      controls.type.forEach((control) => { control.value = ''; });
      controls.availability.forEach((control) => { control.value = ''; });
      controls.price.forEach((control) => { control.value = ''; });
      controls.sale.forEach((control) => { control.checked = false; });
      controls.sort.forEach((control) => { control.value = 'featured'; });
      catalogueState.visible = catalogueState.initial;
      apply();
    }));
    select('[data-catalogue-load]', root)?.addEventListener('click', () => {
      catalogueState.visible += catalogueState.increment;
      apply();
    });
    apply();
  };

  const initializeRecommendations = (root) => {
    if (root.dataset.initialized === 'true' || !root.dataset.url || root.children.length > 0) return;
    root.dataset.initialized = 'true';
    const load = async () => {
      try {
        const response = await fetch(root.dataset.url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const loaded = select('[data-product-recommendations]', parsed);
        if (loaded && loaded.innerHTML.trim()) root.innerHTML = loaded.innerHTML;
      } catch (error) {
        root.hidden = true;
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 300px 0px' });
      observer.observe(root);
    } else {
      load();
    }
  };

  const initializeRoot = (context = document) => {
    selectAll('[data-announcement-bar]', context).forEach(initializeAnnouncement);
    selectAll('[data-hero-slideshow]', context).forEach(initializeHero);
    selectAll('[data-catalogue]', context).forEach(initializeCatalogue);
    selectAll('[data-product-recommendations]', context).forEach(initializeRecommendations);
  };

  document.addEventListener('click', (event) => {
    const genericOpen = event.target.closest('[data-drawer-open]');
    if (genericOpen) {
      const drawer = document.getElementById(genericOpen.dataset.drawerOpen);
      if (drawer) {
        event.preventDefault();
        openInteractiveRoot(drawer, genericOpen);
      }
      return;
    }
    const genericClose = event.target.closest('[data-drawer-close]');
    if (genericClose) {
      const drawer = genericClose.closest('[data-drawer]');
      if (drawer) {
        event.preventDefault();
        closeInteractiveRoot(drawer);
      }
      return;
    }
    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      if (document.body.dataset.cartDrawerEnabled !== 'true') {
        window.location.assign(routes.cart || '/cart');
        return;
      }
      const cartRoot = select('[data-cart-drawer-root]');
      if (cartRoot) openInteractiveRoot(cartRoot, cartOpen);
      return;
    }
    const cartClose = event.target.closest('[data-cart-close]');
    if (cartClose) {
      event.preventDefault();
      closeInteractiveRoot(cartClose.closest('[data-cart-drawer-root]'));
      return;
    }
    const facetsOpen = event.target.closest('[data-facets-open]');
    if (facetsOpen) {
      event.preventDefault();
      const drawer = document.getElementById(facetsOpen.getAttribute('aria-controls'));
      if (drawer) openInteractiveRoot(drawer, facetsOpen);
      return;
    }
    const facetsClose = event.target.closest('[data-facets-close]');
    if (facetsClose) {
      event.preventDefault();
      closeInteractiveRoot(facetsClose.closest('[data-facets-drawer]'));
      return;
    }
    const cartQuantity = event.target.closest('[data-cart-quantity]');
    if (cartQuantity) {
      event.preventDefault();
      handleCartChange(cartQuantity.dataset.line, cartQuantity.dataset.cartQuantity, cartQuantity);
      return;
    }
    const decrease = event.target.closest('[data-quantity-decrease], [data-page-quantity-decrease]');
    if (decrease) {
      event.preventDefault();
      adjustQuantity(decrease, -1);
      return;
    }
    const increase = event.target.closest('[data-quantity-increase], [data-page-quantity-increase]');
    if (increase) {
      event.preventDefault();
      adjustQuantity(increase, 1);
      return;
    }
    const thumbnail = event.target.closest('[data-media-target]');
    if (thumbnail) {
      event.preventDefault();
      const scope = thumbnail.closest('[data-product-section]') || thumbnail.closest('[data-product-gallery]');
      setMedia(scope, thumbnail.dataset.mediaTarget);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
    if (event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;
    event.preventDefault();
    handleAddToCart(form, event.submitter);
  });

  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-product-option]');
    if (option) {
      const section = option.closest('[data-product-section]');
      if (section) updateProductVariant(section);
      return;
    }
    const cartInput = event.target.closest('[data-cart-quantity-input]');
    if (cartInput) {
      const existing = state.cartInputTimers.get(cartInput);
      if (existing) window.clearTimeout(existing);
      const timer = window.setTimeout(() => handleCartChange(cartInput.dataset.line, cartInput.value, cartInput), 350);
      state.cartInputTimers.set(cartInput, timer);
      return;
    }
    const sortSelect = event.target.closest('[data-sort-select]');
    if (sortSelect) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.assign(url.toString());
      return;
    }
    const facetInput = event.target.closest('[data-facets-form] input:not([type="hidden"])');
    if (facetInput) {
      const form = facetInput.closest('[data-facets-form]');
      if (form) form.requestSubmit();
    }
  });

  document.addEventListener('keydown', (event) => {
    const activeRoot = state.activeRoot || select('.cart-drawer.is-open, .facets-drawer.is-open, .drawer.is-open');
    if (!activeRoot) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeInteractiveRoot(activeRoot);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = focusableElements(activeRoot);
    if (!focusable.length) {
      event.preventDefault();
      const panel = select('[role="dialog"]', activeRoot);
      if (panel) panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  reducedMotion.addEventListener('change', () => initializeRoot(document));
  document.addEventListener('shopify:section:load', (event) => initializeRoot(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-hero-slide]');
    if (!slide) return;
    const hero = slide.closest('[data-hero-slideshow]');
    const index = Number(slide.dataset.heroSlide || 0);
    selectAll('[data-hero-slide]', hero).forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    selectAll('[data-hero-dot]', hero).forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  });

  initializeRoot(document);
})();
